/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { DATA_STATES } from 'constants';

import React from 'react';
import PropTypes from 'prop-types';
import { SkeletonText, Loading, EmptyState } from 'carbon-addons-iot-react';
import { injectIntl, defineMessages } from 'react-intl';

import styles from './NoDataGraph.module.css';

const propTypes = {
  state: PropTypes.oneOf(Object.keys(DATA_STATES)).isRequired,
  showSkeletonLoading: PropTypes.bool,
  errorDetails: PropTypes.node,
  iconClassName: PropTypes.string,
};

const defaultProps = {
  showSkeletonLoading: false,
  errorDetails: null,
  iconClassName: styles.nodataGraphIcon,
};

const messages = defineMessages({
  error: 'Error',
  fetching: 'Retrieving data...',
  transient:
    'This data item is transient so it is not graphable. It can be used in subsequent functions.',
  noData: 'No data is available',
  notCalculated:
    'The calculation is scheduled to start within the next 5 minutes. Check back here soon.',
  notCalculatedV2: 'Calculation is in progress and can take up to a minute.',
  timestampNotGraphable:
    'This data item is a timestamp so it is not graphable. It can be used in subsequent functions.',
});

const NoDataGraph = ({
  state,
  showSkeletonLoading,
  errorDetails,
  iconClassName: iconClassName__,
  intl: { formatMessage },
}) => {
  let icon = 'empty';
  let body = '';
  let title;

  switch (state) {
    case DATA_STATES.LOADING:
      return showSkeletonLoading ? (
        <SkeletonText width="50%" className={styles.nodataGraphSkeleton} />
      ) : (
        <>
          <Loading withOverlay={false} />
          {formatMessage(messages.fetching)}
        </>
      );

    case DATA_STATES.DATA_ERROR:
    case DATA_STATES.FUNCTION_ERROR:
      title = formatMessage(messages.error);
      icon = 'error';
      body = errorDetails;
      break;

    case DATA_STATES.TRANSIENT:
      title = formatMessage(messages.transient);
      break;

    case DATA_STATES.NO_DATA:
      title = formatMessage(messages.noData);
      break;

    case DATA_STATES.NOT_CALCULATED:
      title = formatMessage(messages.notCalculated);
      break;

    case DATA_STATES.NOT_CALCULATED_V2:
      title = formatMessage(messages.notCalculatedV2);
      break;

    case DATA_STATES.TIMESTAMP:
    default:
      title = formatMessage(messages.timestampNotGraphable);
      break;
  }

  return <EmptyState body={body} icon={icon} title={title} />;
};

NoDataGraph.propTypes = propTypes;
NoDataGraph.defaultProps = defaultProps;

export default injectIntl(NoDataGraph);
